import { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { Tooltip } from '@nx-frontend/shared/ui/tooltip';
import { getBorderClassNames, getTextClassNames } from '@nx-frontend/shared/themes';
import {
  UiTooltipPosition,
  BucketFile,
  PortalTextType,
  PortalBorderType,
  PortalClassState,
} from '@nx-frontend/shared/types';
import { ThemeColorSchemas, PortalStyleConfig } from '@nx-frontend/shared/types';

export type DocusealState = 'pending' | 'awaiting' | 'complete' | 'declined' | 'other';
interface Submitter {
  slug: string;
  role: string;
  userId: number;
  email: string;
  state: DocusealState;
}
interface DocusealDoc {
  portalId: string;
  workspaceId: string;
  bucketId: string;
  attachmentId: string;
  templateId: number;
  state: DocusealState;
  submitters: Submitter[];
}
interface EsignButtonProps {
  isPdf: boolean;
  userId: number | undefined;
  canCreateEsign: boolean;
  canSubmitEsign: boolean;
  isCustomizerMode?: boolean;
  esignDoc: DocusealDoc | null;
  file: BucketFile;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
}

export const EsignButton: React.FC<EsignButtonProps> = ({
  isPdf,
  canCreateEsign,
  userId,
  canSubmitEsign,
  isCustomizerMode,
  esignDoc,
  file,
  themeColors,
  themeStyle,
}) => {
  const router = useRouter();
  const { toast } = useToast();
  const isSigner = useMemo(() => {
    if (!esignDoc) {
      return false;
    }
    return esignDoc.submitters.find((submitter) => submitter.userId === userId && submitter.state !== 'complete');
  }, [esignDoc, userId]);
  const totalSubmitters = useMemo(() => {
    if (!esignDoc) {
      return 0;
    }
    return esignDoc.submitters.length;
  }, [esignDoc]);
  const completeSubmitters = useMemo(() => {
    if (!esignDoc) {
      return 0;
    }
    return esignDoc.submitters.filter((submitter) => submitter.state === 'complete').length;
  }, [esignDoc]);

  if (!canCreateEsign && !canSubmitEsign) {
    return;
  }

  let btnType = 'none';
  if (isPdf) {
    if (null === esignDoc) {
      if (canCreateEsign) {
        btnType = 'create';
      }
    } else {
      if (esignDoc.state === 'pending') {
        if (canCreateEsign) {
          btnType = 'draft';
        }
      } else if (esignDoc.state === 'awaiting') {
        btnType = isSigner ? 'sign' : 'awaiting';
      } else if (esignDoc.state === 'complete') {
        btnType = 'complete';
      }
    }
  } else {
    if (canCreateEsign) {
      btnType = 'disabledCreate';
    }
  }

  const openCreateEsign = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isCustomizerMode) {
      toast({ title: 'Go to the portal to use the E-signature functionality' });
    } else {
      router.push(`/esign/${file.bucketId}/file/${file.globalId}`);
    }
  };
  const openMakeSign = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isCustomizerMode) {
      toast({ title: 'Go to the portal to use the E-signature functionality' });
    } else {
      if (isSigner) {
        router.push(`/esign/${file.bucketId}/file/${file.globalId}/sign/${isSigner.slug}`);
      }
    }
  };

  let signersList = <> </>;
  if (btnType === 'sign' || btnType === 'awaiting' || btnType === 'complete') {
    if (esignDoc?.submitters && 0 < esignDoc.submitters.length) {
      const awaitingList = esignDoc.submitters.filter((submitter) => submitter.state !== 'complete');
      const completeList = esignDoc.submitters.filter((submitter) => submitter.state === 'complete');
      signersList = (
        <>
          {0 < completeList.length && (
            <div className="text-neutral-300 text-[10px] font-normal uppercase leading-3 tracking-tight mb-2">
              Signed:
            </div>
          )}
          {completeList.map((submitter) => (
            <div className="h-4 justify-start items-center gap-1 inline-flex">
              <div className="w-3 h-3 relative">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 6C2 3.79086 3.79086 2 6 2C8.20914 2 10 3.79086 10 6C10 8.20914 8.20914 10 6 10C3.79086 10 2 8.20914 2 6ZM6 1C3.23858 1 1 3.23858 1 6C1 8.76142 3.23858 11 6 11C8.76142 11 11 8.76142 11 6C11 3.23858 8.76142 1 6 1ZM8.63411 4.82009C8.81089 4.60795 8.78223 4.29267 8.57009 4.11589C8.35795 3.93911 8.04267 3.96777 7.86589 4.17991L5.66298 6.82339L4.29062 5.84314C4.06591 5.68263 3.75364 5.73468 3.59313 5.95938C3.43263 6.18409 3.48467 6.49637 3.70938 6.65687L5.83702 8.17661L8.63411 4.82009Z"
                    fill="#22C55E"
                  />
                </svg>
              </div>
              <div className="text-white text-xs font-normal leading-none">{submitter.email}</div>
            </div>
          ))}
          {0 < completeList.length && 0 < awaitingList.length && <div className="mb-6 w-1"></div>}
          {0 < awaitingList.length && (
            <div className="text-neutral-300 text-[10px] font-normal uppercase leading-3 tracking-tight mb-2">
              Not Signed Yet:
            </div>
          )}
          {awaitingList.map((submitter) => (
            <div className="h-4 justify-start items-center gap-1 inline-flex">
              <div className="w-3 h-3 relative">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M10.5 6C10.5 8.4853 8.4853 10.5 6 10.5C3.51472 10.5 1.5 8.4853 1.5 6C1.5 3.51472 3.51472 1.5 6 1.5C8.4853 1.5 10.5 3.51472 10.5 6Z"
                    stroke="#F59E0B"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeDasharray="2 2"
                  />
                </svg>
              </div>
              <div className="text-white text-xs font-normal leading-none">{submitter.email}</div>
            </div>
          ))}
        </>
      );
    }
  }

  const createEsignBtn = (
    <button
      className={classNames(
        'flex w-[120px] h-9 px-1.5 py-4 items-center justify-center border rounded',
        getTextClassNames(themeColors, PortalTextType.PRIMARY),
        getTextClassNames(themeColors, PortalTextType.PRIMARY_HOVER, [PortalClassState.HOVER]),
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY),
        getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY_HOVER, {
          states: [PortalClassState.HOVER],
        })
      )}
      data-cy="files-createEsign-button"
      onClick={openCreateEsign}
    >
      <span className="text-[13px] font-bold">+ Add eSign </span>
    </button>
  );

  const disabledcreateEsignBtn = (
    <Tooltip
      message="Not applicable for this file format"
      messageClasses="p-2 border-0"
      position={UiTooltipPosition.RIGHT}
    >
      <button
        className={classNames(
          'flex w-[120px] h-9 px-1.5 py-4 items-center justify-center border rounded cursor-default opacity-[0.30]',
          getTextClassNames(themeColors, PortalTextType.SURFACE_DISABLE),
          getBorderClassNames(themeColors, themeStyle, PortalBorderType.SURFACE_DISABLE)
        )}
        data-cy="files-disabledCreateEsign-button"
      >
        <span className="text-[13px] font-bold">+ Add eSign</span>
      </button>
    </Tooltip>
  );

  const signEsignBtn = (
    <div className="group">
      <button
        className={classNames(
          'flex w-[82px] h-9 px-1.5 py-4 items-center justify-center border rounded',
          getTextClassNames(themeColors, PortalTextType.PRIMARY),
          getTextClassNames(themeColors, PortalTextType.PRIMARY_HOVER, [PortalClassState.HOVER]),
          getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY),
          getBorderClassNames(themeColors, themeStyle, PortalBorderType.PRIMARY_HOVER, {
            states: [PortalClassState.HOVER],
          })
        )}
        data-cy="files-createEsign-button"
        onClick={openMakeSign}
      >
        <span className="text-[13px] font-bold">Sign</span>
      </button>
    </div>
  );

  const pendingEsign = (
    <button
      data-cy="files-pendingEsign-button"
      className="flex h-9 px-1.5 py-4 items-center rounded gap-1.5 text-black/90 bg-neutral-100"
      onClick={openCreateEsign}
    >
      <Icon icon={IconsMap['DRAFT']} className="text-[#737373] w-4 h-4" />
      <span className="text-[10px]">DRAFT</span>
    </button>
  );

  const awaitingEsign = (
    <Tooltip message={signersList} messageClasses="py-3 px-4 border-0" position={UiTooltipPosition.TOP}>
      <button
        className="flex h-9 px-1.5 py-4 items-center rounded gap-1.5 text-black/90 bg-amber-100"
        data-cy="files-awaitingEsign-button"
        onClick={openCreateEsign}
      >
        {0 === completeSubmitters && <Icon icon={IconsMap['SIGN_WAIT_ZERO2']} className="text-[#A16207] w-4 h-4" />}
        {0 < completeSubmitters && <Icon icon={IconsMap['SIGN_WAIT2']} className="text-[#A16207] w-4 h-4" />}
        <span className="text-[10px]">
          {completeSubmitters} / {totalSubmitters} SIGNED
        </span>
      </button>
    </Tooltip>
  );

  const completeEsign = (
    <Tooltip message={signersList} messageClasses="py-3 px-4 border-0" position={UiTooltipPosition.TOP}>
      <button
        className="flex h-9 px-1.5 py-4 items-center rounded gap-1.5 bg-green-100 text-black/90"
        data-cy="files-readyEsign-button"
        onClick={openCreateEsign}
      >
        <Icon icon={IconsMap['SIGN_READY2']} className="text-[#15803D] w-4 h-4" />
        <span className="text-[10px]">
          {completeSubmitters} / {totalSubmitters} SIGNED
        </span>
      </button>
    </Tooltip>
  );

  return (
    <div className="flex max-w-full min-w-0">
      {btnType === 'create' && createEsignBtn}
      {btnType === 'sign' && signEsignBtn}
      {btnType === 'draft' && pendingEsign}
      {btnType === 'awaiting' && awaitingEsign}
      {btnType === 'complete' && completeEsign}
      {btnType === 'disabledCreate' && disabledcreateEsignBtn}
    </div>
  );
};
